import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import fetcher from "../utils/fetcher";
import { formatCurrency } from "../utils/numbers";
import { toast } from "../components/shadcn/useToast";
import {
  ContactInfo,
  EnumProductTypes,
  Listing,
  OwnerContactInfo,
  Tag,
  Note,
  SummaryListing,
} from "../types/listing";
import {
  getClientNoteFromServerNote,
  getDisplayedContacts,
} from "src/utils/listings";
import {
  createAddressFromComponents,
  formatAddressForUrl,
} from "src/utils/neighborhoodSearch";
import {
  getDistressedListingPrice,
  getDistressedListingStatus,
  getDistressedListingTitle,
  getExpiredListingStatus,
  getExpiredListingTitle,
  getFsboListingPrice,
  getFsboListingStatus,
  getFsboListingTitle,
  getImportedListingTitle,
  getListingPrice,
  getPreforeclosureListingPrice,
  getPreForeclosureListingStatus,
  getPreforeclosureListingTitle,
  getStatusDateFromMlsStatus,
} from "src/utils/listings";
import { SUPPORTED_SOCIAL_MEDIA } from "src/constants/listing";
import { pick } from "src/utils/objects";
import { PropertyTypeEnum, StatusTypeEnum } from "src/data/selectionData";
import { stringToNumber } from "src/utils/strings";
import {
  formatAsCurrency,
  formatNumber,
  formatNumbersInString,
} from "src/utils/numbers";
import formatPhoneNumber from "src/components/common/formatPhones";
import { formatDate } from "../utils/dates";
import capitalizeWords from "src/components/common/capitalizeWords";
import {
  LISTING_DETAILS_QUERY_KEY,
  SEARCH_LISTING_QUERY_KEY,
} from "src/constants/cache";
import { propertiesApi, leadsApi } from "../libs/landvoice-api";
import { getListingFromLead } from "../utils/neighborhoodSearch";

/**
 * Creates ContactInfo array from server listing data with product-specific logic
 */
const createContactsFromServerListing = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  serverListing: Record<string, any>,
  commonProps: { id: string; propertyId?: string },
  productType: EnumProductTypes
): ContactInfo[] => {
  if (!serverListing.contacts) return [];

  return serverListing.contacts.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (contact: Record<string, any>, index: number): ContactInfo => {
      // Base contact info that's common across all product types
      const baseContact = {
        id: contact.id || index,
        listingId: commonProps.id,
        propertyId:
          productType === EnumProductTypes.fsbo
            ? serverListing.property_id
            : commonProps.propertyId,
        fullName: capitalizeWords([contact?.first_name, contact?.last_name].filter(Boolean).join(" ")) || "",
        firstName: capitalizeWords(contact?.first_name) || "",
        lastName: capitalizeWords(contact?.last_name) || "",
        phone: contact?.phone,
        formattedPhone: formatPhoneNumber(contact?.phone),
        phoneType: contact?.phone_type,
        hidden: contact?.hidden,
      };

      // Add product-specific fields
      const productSpecificFields: Record<string, unknown> = {};

      switch (productType) {
        case EnumProductTypes.fsbo:
          productSpecificFields.dnc = contact?.dnc;
          break;
        default:
          productSpecificFields.dnc = contact?.phone_dnc;
          break;
      }
      return { ...baseContact, ...productSpecificFields } as ContactInfo;
    }
  );
};

export const convertServerToClientSummaryListing = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  serverListing: Record<string, any>,
  productType: EnumProductTypes
): SummaryListing => {
  return {
    id: String(serverListing.id),
    geolocation: serverListing?.property?.geolocation
      ? {
          lat: stringToNumber(serverListing.property.geolocation.latitude),
          lng: stringToNumber(serverListing.property.geolocation.longitude),
        }
      : undefined,
    productType,
  };
};

export const convertServerToClientListing = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  serverListing: Record<string, any>,
  productType: EnumProductTypes
): Listing | null => {
  try {
    const commonProps = {
      id: serverListing.id,
      propertyId: serverListing.property?.id,
      productType: serverListing.record_type,
      notes: serverListing?.notes?.map(getClientNoteFromServerNote),
      tags: serverListing?.tags,
      typeCode: serverListing.property_type,
      type:
        PropertyTypeEnum?.[
          serverListing.property_type as keyof typeof PropertyTypeEnum
        ] || "",
      geolocation: serverListing?.property?.geolocation
        ? {
            lat: stringToNumber(serverListing.property.geolocation.latitude),
            lng: stringToNumber(serverListing.property.geolocation.longitude),
          }
        : undefined,
      statusCode:
        StatusTypeEnum?.[
          serverListing.status as keyof typeof StatusTypeEnum
        ] || "",
      publishDate: serverListing.publish_date,
      formattedPublishDate: formatDate(serverListing.publish_date),
      contacts: getDisplayedContacts(
        createContactsFromServerListing(
          serverListing,
          { id: serverListing.id, propertyId: serverListing.property?.id },
          productType
        )
      ),
    };

    switch (productType) {
      case EnumProductTypes.customupload:
        const getImportedOwnerInfo = () => {
          const { owner = {}, social_media: socialMedia = {} } = serverListing;
          const { mailing_property: address = {} } = owner || {};
          const {
            street_address: streetAddress = "",
            city = "",
            state = "",
            postal_code: postalCode = "",
          } = address || {};
          const { email = "" } = socialMedia || {};

          const ownerAddress = createAddressFromComponents({
            streetAddress,
            city,
            state,
            postalCode,
          });

          return {
            id: owner?.id || serverListing.id,
            fullName: capitalizeWords(owner?.full_name) || "",
            firstName: capitalizeWords(owner?.first_name) || '',
            lastName: capitalizeWords(owner?.last_name) || '',
            email,
            phone: owner?.phone,
            formattedPhone: formatPhoneNumber(owner?.phone),
            address: ownerAddress,
            ...pick(socialMedia || {}, SUPPORTED_SOCIAL_MEDIA),
          } as OwnerContactInfo;
        };

        return {
          ...commonProps,
          title: getImportedListingTitle(serverListing),
          bath: serverListing.baths || 0,
          beds: serverListing.bedrooms || 0,
          sqrFeet: serverListing.square_footage || 0,
          formattedSqrFeet: formatNumbersInString(serverListing.square_footage),
          lotSize: serverListing.lot_size || 0,
          formattedLotSize: formatNumbersInString(serverListing.lot_size),
          // TODO Ask chris about this
          yearBuilt: serverListing.property_features?.year_built,
          mapLink: (() => {
            const address = createAddressFromComponents({
              streetAddress: serverListing.property?.street_address,
              city: serverListing.property?.city,
              state: serverListing.property?.state,
              postalCode: serverListing.property?.postal_code,
            });
            const urlAddress = formatAddressForUrl(address);
            return urlAddress
              ? `https://www.google.com/maps/place/${urlAddress}`
              : "";
          })(),
          extraFields: serverListing?.extra_fields,
          address: createAddressFromComponents({
            streetAddress: serverListing.property?.street_address,
            city: serverListing.property?.city,
            state: serverListing.property?.state,
            postalCode: serverListing.property?.postal_code,
          }),
          ownerContactInfo: getImportedOwnerInfo(),
          price: parseFloat(serverListing?.price || 0),
          formattedPrice: formatCurrency(parseFloat(serverListing?.price || 0)),
        };

      case EnumProductTypes.preforeclosure:
        return {
          ...commonProps,
          address: createAddressFromComponents({
            streetAddress: serverListing.property?.street_address,
            city: serverListing.property?.city,
            state: serverListing.property?.state,
            postalCode: serverListing.property?.postal_code,
          }),
          auctionDate: serverListing.auction_date,
          formattedAuctionDate: formatDate(serverListing.auction_date),
          loanAmount: serverListing.loan_amount,
          formattedLoanAmount: formatAsCurrency(
            serverListing.loan_amount,
            "--"
          ),
          mortgageHolder: serverListing.mortgage_holder,
          countyRecorded: serverListing.county_recorded,
          attorneyName: serverListing.attorney_name,
          mortgageDate: serverListing.mortgage_date,
          delinquentAmount: serverListing.delinquent_amount,
          formattedDelinquentAmount: formatAsCurrency(
            serverListing.delinquent_amount,
            "--"
          ),
          delinquentDate: serverListing.delinquent_date,
          dateRecorded: serverListing.date_recorded,
          formattedDateRecorded: formatDate(serverListing.date_recorded),
          attorneyPhone: serverListing.attorney_phone,
          formattedAttorneyPhone: formatPhoneNumber(
            serverListing.attorney_phone
          ),
          trusteeSaleNumber: serverListing.trustee_sale_number,
          auctionLocation: serverListing.auction_location,
          auctionMinimumBid: serverListing.auction_min_bid,
          formattedAuctionMinimumBid: formatAsCurrency(
            serverListing.auction_min_bid,
            "--"
          ),
          formattedDelinquentDate: formatDate(serverListing.delinquent_date),
          formattedMortgageDate: formatDate(serverListing.mortgage_date),
          bath: serverListing.property_features.full_bathrooms,
          beds: serverListing.property_features.bedrooms,
          yearBuilt: serverListing.property_features?.year_built,
          dom: serverListing.days_on_market,
          lotSize: serverListing.property_features?.acres,
          formattedLotSize: formatNumbersInString(
            serverListing.property_features?.acres
          ),
          mapLink: (() => {
            const address = createAddressFromComponents({
              streetAddress: serverListing.property?.street_address,
              city: serverListing.property?.city,
              state: serverListing.property?.state,
              postalCode: serverListing.property?.postal_code,
            });
            const urlAddress = formatAddressForUrl(address);
            return urlAddress
              ? `https://www.google.com/maps/place/${urlAddress}`
              : "";
          })(),
          price: getPreforeclosureListingPrice(serverListing),
          formattedPrice: getPreforeclosureListingPrice(serverListing)
            ? formatCurrency(getPreforeclosureListingPrice(serverListing))
            : "--",
          sqrFeet: serverListing.property_features?.total_square_footage,
          formattedSqrFeet: formatNumbersInString(
            serverListing.property_features?.total_square_footage
          ),
          status: getPreForeclosureListingStatus(serverListing),
          title: getPreforeclosureListingTitle(serverListing),
          ownerContactInfo: {
            id: serverListing.id,
            fullName: capitalizeWords(serverListing.owner_name) || "",
            firstName: capitalizeWords(serverListing.owner_first_name) || '',
            lastName: capitalizeWords(serverListing.owner_last_name) || '',
            email: serverListing.email,
            address: serverListing?.street_address
              ? createAddressFromComponents({
                  streetAddress: serverListing.street_address,
                  city: serverListing.city,
                  state: serverListing.state,
                  postalCode: serverListing.postal_code,
                })
              : {},
          } as OwnerContactInfo,
        };

      case EnumProductTypes.distressed:
        const getDistressedOwnerInfo = () => {
          const owner = serverListing.owner ?? {};
          const socialMedia = serverListing.social_media ?? {};
          const {
            full_name: fullName = "",
            first_name: firstName = "",
            last_name: lastName = "",
            mailing_property: address = {},
          } = owner;
          const {
            street_address: streetAddress = "",
            city = "",
            state = "",
            postal_code: postalCode = "",
          } = address || {};

          return {
            id: serverListing.owner?.id || serverListing.id,
            fullName: capitalizeWords(fullName) || "",
            firstName: capitalizeWords(firstName) || "",
            lastName: capitalizeWords(lastName) || "",
            email: socialMedia?.email || "",
            address: createAddressFromComponents({
              streetAddress,
              city,
              state,
              postalCode,
            }),
            ...pick(socialMedia || {}, SUPPORTED_SOCIAL_MEDIA),
          } as OwnerContactInfo;
        };

        return {
          ...commonProps,
          auctionDate: serverListing.auction_date,
          formattedAuctionDate: formatDate(serverListing.auction_date),
          bath: serverListing.property_features?.full_bathrooms,
          beds: serverListing.property_features?.bedrooms,
          yearBuilt: serverListing.property_features?.year_built,
          dom: serverListing.days_on_market,
          lotSize: serverListing.property_features?.acres,
          formattedLotSize: formatNumbersInString(
            serverListing.property_features?.acres
          ),
          mapLink: (() => {
            const address = createAddressFromComponents({
              streetAddress: serverListing.property?.street_address,
              city: serverListing.property?.city,
              state: serverListing.property?.state,
              postalCode: serverListing.property?.postal_code,
            });
            const urlAddress = formatAddressForUrl(address);
            return urlAddress
              ? `https://www.google.com/maps/place/${urlAddress}`
              : "";
          })(),
          price: getDistressedListingPrice(serverListing),
          formattedPrice: formatCurrency(getDistressedListingPrice(serverListing)),
          sqrFeet: serverListing.property_features?.total_square_footage,
          formattedSqrFeet: formatNumbersInString(
            serverListing.property_features?.total_square_footage
          ),
          status: getDistressedListingStatus(serverListing),
          title: getDistressedListingTitle(serverListing),
          ownerContactInfo: getDistressedOwnerInfo(),
          address: createAddressFromComponents({
            streetAddress: serverListing.property?.street_address,
            city: serverListing.property?.city,
            state: serverListing.property?.state,
            postalCode: serverListing.property?.postal_code,
          }),
          distressedLevel: serverListing.distressed_level,
          isDistressed: true,
        };

      case EnumProductTypes.expired:
      case EnumProductTypes.oldexpired:
        const getExpiredOwnerInfo = () => {
          const {
            owner_information: owner = {},
            social_media: socialMedia = {},
          } = serverListing;
          const {
            full_name: fullName = "",
            first_name: firstName = "",
            last_name: lastName = "",
            property: address = {}
          } = owner || {};
          const {
            street_address: streetAddress = "",
            city = "",
            state = "",
            postal_code: postalCode = "",
          } = address || {};

          const { email = "" } = socialMedia || {};
          return {
            id: owner?.id || serverListing.id,
            fullName: capitalizeWords(fullName) || "",
            firstName: capitalizeWords(firstName) || "",
            lastName: capitalizeWords(lastName) || "",
            email,
            address: createAddressFromComponents({
              streetAddress,
              city,
              state,
              postalCode,
            }),
            socials: pick(socialMedia, SUPPORTED_SOCIAL_MEDIA),
          } as OwnerContactInfo;
        };

        return {
          ...commonProps,
          bath: serverListing.baths || serverListing.bathrooms,
          beds: serverListing.bedrooms,
          yearBuilt: serverListing.year_built,
          dom: serverListing.days_on_market,
          hasDom: true,
          lotSize: serverListing.acres || serverListing.property_size,
          formattedLotSize: formatNumbersInString(
            serverListing.acres || serverListing.property_size
          ),
          mapLink: (() => {
            const address = createAddressFromComponents({
              streetAddress: serverListing.property?.street_address,
              city: serverListing.property?.city,
              state: serverListing.property?.state,
              postalCode: serverListing.property?.postal_code,
            });
            const urlAddress = formatAddressForUrl(address);
            return urlAddress
              ? `https://www.google.com/maps/place/${urlAddress}`
              : "";
          })(),
          price: getListingPrice(serverListing),
          formattedPrice: getListingPrice(serverListing)
            ? formatCurrency(getListingPrice(serverListing))
            : "--",
          sqrFeet: formatNumber(
            serverListing.square_feet || serverListing.square_footage,
            "--"
          ),
          formattedSqrFeet: formatNumbersInString(
            serverListing.square_feet || serverListing.square_footage
          ),
          status: getExpiredListingStatus(serverListing),
          title: getExpiredListingTitle(serverListing),
          ownerContactInfo: getExpiredOwnerInfo(),
          taxId: serverListing.tax_id,
          ownerOccupied: serverListing.owner_occupied,
          mlsNumber: serverListing.mls_number,
          listAgent: serverListing.list_agent,
          listAgentPhone: serverListing.list_agent_phone,
          formattedListAgentPhone: formatPhoneNumber(
            serverListing.list_agent_phone
          ),
          listAgentCompany: serverListing.list_agent_company,
          statusDate: serverListing.status_date,
          formattedStatusDate: formatDate(serverListing.status_date),
          daysOnMarket: serverListing.days_on_market,
          showingInstructions: serverListing.showing_instructions,
          comments: serverListing.comments,
          address: createAddressFromComponents({
            streetAddress: serverListing.property?.street_address,
            city: serverListing.property?.city,
            state: serverListing.property?.state,
            postalCode: serverListing.property?.postal_code,
          }),
        };

      case EnumProductTypes.fsbo:
        const getFsboOwnerInfo = (): OwnerContactInfo => {
          return {
            id: serverListing.contacts[0]?.id || serverListing.id,
            fullName: serverListing.contacts[0]?.first_name
              ? capitalizeWords(`${serverListing.contacts[0]?.first_name || ""} ${
                  serverListing.contacts[0]?.last_name || ""
                }`.trim()) || ""
              : "",
            firstName: capitalizeWords(serverListing.contacts[0]?.first_name) || '',
            lastName: capitalizeWords(serverListing.contacts[0]?.last_name) || '',
            email: serverListing.contacts[0]?.email,
            formattedPhone: formatPhoneNumber(serverListing.contacts[0]?.phone),
            address: createAddressFromComponents({
              streetAddress: serverListing.contacts[0]?.street_address,
              city: serverListing.contacts[0]?.city,
              state: serverListing.contacts[0]?.state,
              postalCode: serverListing.contacts[0]?.postal_code,
            }),
            socials: pick(
              {
                ...serverListing?.owner_information,
                ...serverListing.social_media,
              },
              SUPPORTED_SOCIAL_MEDIA
            ),
          } as OwnerContactInfo;
        };

        return {
          ...commonProps,
          address: createAddressFromComponents({
            streetAddress: serverListing.street_address,
            city: serverListing.city,
            state: serverListing.state,
            postalCode: serverListing.postal_code,
          }),
          bath: serverListing.baths || serverListing.bathrooms,
          beds: serverListing.bedrooms,
          yearBuilt: serverListing.year_built,
          id: serverListing.publish_id,
          propertyId: serverListing.property_id,
          lotSize: serverListing?.acres || serverListing?.property_size,
          formattedLotSize: formatNumbersInString(
            serverListing?.acres || serverListing?.property_size
          ),
          mapLink: (() => {
            const address = createAddressFromComponents({
              streetAddress: serverListing.street_address,
              city: serverListing.city,
              state: serverListing.state,
              postalCode: serverListing.postal_code,
            });
            const urlAddress = formatAddressForUrl(address);
            return urlAddress
              ? `https://www.google.com/maps/place/${urlAddress}`
              : "";
          })(),
          price: getFsboListingPrice(serverListing),
          formattedPrice: getFsboListingPrice(serverListing)
            ? formatCurrency(getFsboListingPrice(serverListing))
            : "--",
          sqrFeet: serverListing.square_feet || serverListing.square_footage,
          formattedSqrFeet: formatNumbersInString(
            serverListing.square_feet || serverListing.square_footage
          ),
          status: getFsboListingStatus(serverListing),
          statusDate: getStatusDateFromMlsStatus(serverListing),
          title: getFsboListingTitle(serverListing),
          ownerContactInfo: getFsboOwnerInfo(),
          adText: serverListing.ad_text,
          geolocation: serverListing?.geolocation
          ? {
              lat: stringToNumber(serverListing.geolocation.latitude),
              lng: stringToNumber(serverListing.geolocation.longitude),
            }
          : undefined,
        };

      default:
        console.error("Invalid product type", productType);
        return null;
    }
  } catch (error) {
    console.error("Error converting server listing to client listing", error);
    return null;
  }
};

/**
 * Get the grouping value for a listing based on the sort field.
 * Maps API sort field names to listing properties.
 */
const getSortFieldValue = (listing: Listing, sortField: string): string | number => {
  switch (sortField) {
    case "date":
      return listing.formattedPublishDate || "";
    case "status_date":
      return listing.statusDate || "";
    case "price":
      return listing.price || 0;
    case "dom":
      return listing.daysOnMarket || 0;
    case "city":
      return listing.address?.city || "";
    case "state":
      return listing.address?.state || "";
    case "postal_code":
      return listing.address?.postalCode || "";
    case "sq_ft":
      return listing.sqrFeet || 0;
    case "lot_size":
      return listing.lotSize || "";
    case "year_built":
      return listing.yearBuilt || "";
    case "auction_date":
      return listing.auctionDate || "";
    default:
      return listing.formattedPublishDate || "";
  }
};

/**
 * Sort listings by contact priority within each group (based on sort field).
 * Priority: non-DNC contacts (2) > DNC-only contacts (1) > no contacts (0)
 * Preserves server sort order between groups and within same priority.
 */
export const sortListingsByContacts = (
  listingsToSort: Listing[],
  sortField = "date",
): Listing[] => {
  const indexed = listingsToSort.map((listing, index) => ({ listing, index }));

  const getContactPriority = (listing: Listing) => {
    const contacts = listing.contacts || [];
    if (contacts.length === 0) return 0;
    if (contacts.some(c => !c.dnc)) return 2;
    return 1;
  };

  indexed.sort((a, b) => {
    // Get group values based on sort field
    const aGroup = getSortFieldValue(a.listing, sortField);
    const bGroup = getSortFieldValue(b.listing, sortField);

    // If different groups, preserve original server order
    if (aGroup !== bGroup) {
      return a.index - b.index;
    }

    // Within the same group, sort by contact priority
    const aPriority = getContactPriority(a.listing);
    const bPriority = getContactPriority(b.listing);
    const priorityDiff = bPriority - aPriority;

    // If priority is the same, preserve original server order
    if (priorityDiff === 0) {
      return a.index - b.index;
    }

    return priorityDiff;
  });

  return indexed.map(item => item.listing);
};

export const useSearchListingsQuery = ({
  searchQueryKey,
  searchApiQueryParams,
  currentPage,
  pageSize,
  sortField,
  sortDir,
  searchUrl,
  productType,
}: {
  searchQueryKey: string;
  searchApiQueryParams: URLSearchParams;
  currentPage: number;
  pageSize: number;
  sortField: string;
  sortDir: string;
  searchUrl: string;
  productType: EnumProductTypes;
}) => {
  return useQuery({
    queryKey: [
      SEARCH_LISTING_QUERY_KEY,
      searchQueryKey,
      searchApiQueryParams.toString(),
    ],
    enabled: !!(currentPage && pageSize && sortField && sortDir),
    retry: false,
    queryFn: async () => {
      try {
        const resp = await fetcher({
          url: `${searchUrl}?${searchApiQueryParams.toString()}`,
        });

        const { listings, ...others } = resp.data;
        const convertedListings = resp.data.listings.reduce(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (acc: Listing[], serverListing: Record<string, any>) => {
            const convertedListing = convertServerToClientListing(
              serverListing,
              productType
            );
            if (convertedListing) {
              acc.push(convertedListing);
            }
            return acc;
          },
          []
        );

        // Secondary sort: prioritize listings with contacts within each sort group
        // Order: non-DNC contacts first, then DNC-only contacts, then no contacts
        const sortedListings = sortListingsByContacts(convertedListings, sortField);

        return {
          listings: sortedListings,
          ...others,
        };
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Failed to load properties",
          description:
            "There was an unexpected error loading properties. Please try again later.",
        });
        throw error;
      }
    },
  });
};

export const useListingDetailsQuery = (
  productType: EnumProductTypes,
  listingId: string,
  options?: { enabled?: boolean; isHideDNCPhoneNumbers?: boolean }
): UseQueryResult<Listing> => {
  const isNeighborhoodSearch = productType === EnumProductTypes.ns;

  return useQuery({
    queryKey: [LISTING_DETAILS_QUERY_KEY, listingId, options?.isHideDNCPhoneNumbers],
    queryFn: async () => {
      let listing: Listing;

      if (isNeighborhoodSearch) {
        const { data: lead } = await leadsApi.getLead(listingId);
        listing = getListingFromLead(lead);
      } else {
        const resp = await fetcher({
          url: `/api/${productType}/listing/${listingId}/`,
        });
        listing = convertServerToClientListing(resp.data, productType) as Listing;
      }

      // Filter out DNC contacts if isHideDNCPhoneNumbers is true
      if (options?.isHideDNCPhoneNumbers && listing.contacts) {
        listing = {
          ...listing,
          contacts: listing.contacts.filter(contact => !contact.dnc)
        };
      }

      return listing;
    },
    enabled: !!listingId && (options?.enabled !== false),
  });
};

export const useListingUpdateTagsMutation = ({
  productType,
}: {
  productType: EnumProductTypes;
}) => {
  const queryClient = useQueryClient();
  const isNeighborhoodSearch = productType === EnumProductTypes.ns;

  return useMutation({
    mutationKey: ["saveListingTags"],
    mutationFn: async (params: {
      listingId: string;
      tags?: Tag[];
      previousTags?: Tag[];
    }) => {
      if (isNeighborhoodSearch) {
        const { listingId, tags = [], previousTags = [] } = params;

        const deletedTags = previousTags.filter(
          (tag: string) => !tags.includes(tag)
        );
        const addedTags = tags.filter(
          (tag: string) => !previousTags.includes(tag)
        );

        const promises: Promise<unknown>[] = [];

        deletedTags.forEach((tag: string) => {
          promises.push(propertiesApi.deleteTag(listingId, tag));
        });

        addedTags.forEach((tag: string) => {
          promises.push(propertiesApi.addTag(listingId, tag));
        });

        return Promise.all(promises);
      } else {
        const { listingId, tags } = params;
        if (!tags) throw new Error("tags are required");

        const resp = await fetcher({
          url:
            productType === EnumProductTypes.fsbo
              ? `/api/fsbo/listing/${listingId}`
              : `/api/${productType}/listing/${listingId}/tags`,
          method: "POST",
          body: { tags },
        });
        return resp.data;
      }
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({
        predicate: (query) => query.queryKey[0] === SEARCH_LISTING_QUERY_KEY,
      });
      await queryClient.cancelQueries({
        predicate: (query) => query.queryKey[0] === LISTING_DETAILS_QUERY_KEY,
      });

      const previousListings = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === SEARCH_LISTING_QUERY_KEY,
      });
      const previousDetails = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === LISTING_DETAILS_QUERY_KEY,
      });

      const idToMatch = params.listingId;
      const idField = isNeighborhoodSearch ? "propertyId" : "id";

      // Update all matching search listing queries
      queryClient
        .getQueriesData({
          predicate: (query) => query.queryKey[0] === SEARCH_LISTING_QUERY_KEY,
        })
        .forEach(([queryKey, data]) => {
          if (!data) return;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          queryClient.setQueryData(queryKey, (old: any) => {
            if (!old) return old;
            // Handle infinite query data structure
            if ("pages" in old) {
              return {
                ...old,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                pages: (old.pages || []).map((page: any) =>
                  Array.isArray(page)
                    ? page.map((listing: Listing) =>
                        listing[idField as keyof Listing] === idToMatch
                          ? { ...listing, tags: params.tags }
                          : listing
                      )
                    : page
                ),
              };
            }
            // Handle regular query data structure for neighborhood search
            if (Array.isArray(old)) {
              return old.map((listing: Listing) =>
                listing[idField as keyof Listing] === idToMatch
                  ? { ...listing, tags: params.tags }
                  : listing
              );
            }
            // Handle regular query structure for other product types
            return {
              ...old,
              listings: (old.listings || []).map((listing: Listing) =>
                listing[idField as keyof Listing] === idToMatch
                  ? { ...listing, tags: params.tags }
                  : listing
              ),
            };
          });
        });

      // Update listing details cache
      const detailsQueries = queryClient.getQueriesData({
        queryKey: [LISTING_DETAILS_QUERY_KEY],
      });
      detailsQueries.forEach(([queryKey, data]) => {
        const listing = data as Listing;
        if (listing?.[idField as keyof Listing] === idToMatch) {
          queryClient.setQueryData(queryKey, {
            ...listing,
            tags: params.tags,
          });
        }
      });

      return { previousListings, previousDetails };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousListings) {
        context.previousListings.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousDetails) {
        context.previousDetails.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SEARCH_LISTING_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [LISTING_DETAILS_QUERY_KEY] });
    },
  });
};

export const useListingAddNoteMutation = ({
  productType,
}: {
  productType: EnumProductTypes;
}) => {
  const queryClient = useQueryClient();
  const isNeighborhoodSearch = productType === EnumProductTypes.ns;

  return useMutation({
    mutationKey: ["saveListingNote"],
    mutationFn: async ({
      listingId,
      note,
    }: {
      listingId: string;
      note: string;
    }) => {
      if (isNeighborhoodSearch) {
        const resp = await propertiesApi.createNote(listingId, note);
        return resp.data;
      } else {
        const resp = await fetcher({
          url: `/api/${productType}/listing/${listingId}`,
          method: "POST",
          body: JSON.stringify({
            title: new Date().getTime(),
            note: note,
          }),
        });
        return resp.data;
      }
    },
    onMutate: async ({ listingId, note }) => {
      const idToMatch = listingId;
      const idField = isNeighborhoodSearch ? "propertyId" : "id";

      await queryClient.cancelQueries({ queryKey: [SEARCH_LISTING_QUERY_KEY] });
      await queryClient.cancelQueries({
        queryKey: [LISTING_DETAILS_QUERY_KEY],
      });

      const previousListings = queryClient.getQueryData([
        SEARCH_LISTING_QUERY_KEY,
      ]);
      const previousDetails = queryClient.getQueryData([
        LISTING_DETAILS_QUERY_KEY,
      ]);

      // Create optimistic note with temporary ID
      const optimisticId = `optimistic-${Date.now()}`;
      const newNote: Note = {
        id: optimisticId,
        note,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        formattedCreatedAt: isNeighborhoodSearch
          ? formatDate(new Date().toISOString())
          : new Date().toLocaleString(),
        formattedUpdatedAt: isNeighborhoodSearch
          ? formatDate(new Date().toISOString())
          : new Date().toLocaleString(),
      };

      // Update listing details cache - prepend the new note
      const detailsQueries = queryClient.getQueriesData({
        queryKey: [LISTING_DETAILS_QUERY_KEY],
      });
      detailsQueries.forEach(([queryKey, data]) => {
        const listing = data as Listing;
        if (listing?.[idField as keyof Listing] === idToMatch) {
          queryClient.setQueryData(queryKey, {
            ...listing,
            notes: [newNote, ...(listing.notes || [])],
          });
        }
      });

      // Also update search listings cache so ManageNotes dialog shows the new note
      const searchQueries = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === SEARCH_LISTING_QUERY_KEY,
      });
      searchQueries.forEach(([queryKey, data]) => {
        if (!data) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueryData(queryKey, (old: any) => {
          if (!old?.listings) return old;
          return {
            ...old,
            listings: old.listings.map((listing: Listing) =>
              listing[idField as keyof Listing] === idToMatch
                ? { ...listing, notes: [newNote, ...(listing.notes || [])] }
                : listing
            ),
          };
        });
      });

      return { previousListings, previousDetails, optimisticId, listingId };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousListings) {
        queryClient.setQueryData(
          [SEARCH_LISTING_QUERY_KEY],
          context.previousListings
        );
      }
      if (context?.previousDetails) {
        queryClient.setQueryData(
          [LISTING_DETAILS_QUERY_KEY],
          context.previousDetails
        );
      }
    },
    onSuccess: (serverNote, _variables, context) => {
      const idField = isNeighborhoodSearch ? "propertyId" : "id";
      const idToMatch = context?.listingId;
      const realNote = getClientNoteFromServerNote(serverNote);

      // Replace optimistic note with server note in listing details cache
      const detailsQueries = queryClient.getQueriesData({
        queryKey: [LISTING_DETAILS_QUERY_KEY],
      });
      detailsQueries.forEach(([queryKey, data]) => {
        const listing = data as Listing;
        if (listing?.[idField as keyof Listing] === idToMatch) {
          queryClient.setQueryData(queryKey, {
            ...listing,
            notes: (listing.notes || []).map((n) =>
              n.id === context?.optimisticId ? realNote : n
            ),
          });
        }
      });

      // Also update search listings cache
      const searchQueries = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === SEARCH_LISTING_QUERY_KEY,
      });
      searchQueries.forEach(([queryKey, data]) => {
        if (!data) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueryData(queryKey, (old: any) => {
          if (!old?.listings) return old;
          return {
            ...old,
            listings: old.listings.map((listing: Listing) =>
              listing[idField as keyof Listing] === idToMatch
                ? {
                    ...listing,
                    notes: (listing.notes || []).map((n: Note) =>
                      n.id === context?.optimisticId ? realNote : n
                    ),
                  }
                : listing
            ),
          };
        });
      });
    },
  });
};

export const useListingDeleteNoteMutation = ({
  productType,
}: {
  productType: EnumProductTypes;
}) => {
  const queryClient = useQueryClient();
  const isNeighborhoodSearch = productType === EnumProductTypes.ns;

  return useMutation({
    mutationKey: ["deleteListingNote"],
    mutationFn: async ({
      noteId,
      listingId,
    }: {
      noteId: string;
      listingId: string;
    }) => {
      if (isNeighborhoodSearch) {
        return propertiesApi.deleteNote(listingId, noteId);
      } else {
        return fetcher({
          url: `/api/${productType}/note/${noteId}`,
          method: "DELETE",
        });
      }
    },
    onMutate: async ({ noteId, listingId }) => {
      await queryClient.cancelQueries({ queryKey: [SEARCH_LISTING_QUERY_KEY] });
      await queryClient.cancelQueries({
        queryKey: [LISTING_DETAILS_QUERY_KEY],
      });

      const previousListings = queryClient.getQueryData([
        SEARCH_LISTING_QUERY_KEY,
      ]);
      const previousDetails = queryClient.getQueryData([
        LISTING_DETAILS_QUERY_KEY,
      ]);

      const idToMatch = listingId;
      const idField = isNeighborhoodSearch ? "propertyId" : "id";

      // Update listing details cache
      const detailsQueries = queryClient.getQueriesData({
        queryKey: [LISTING_DETAILS_QUERY_KEY],
      });
      detailsQueries.forEach(([queryKey, data]) => {
        const listing = data as Listing;
        if (listing?.[idField as keyof Listing] === idToMatch) {
          queryClient.setQueryData(queryKey, {
            ...listing,
            notes: listing.notes?.filter((n) => n.id !== noteId) || [],
          });
        }
      });

      // Also update search listings cache so ManageNotes dialog shows the removal immediately
      const searchQueries = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === SEARCH_LISTING_QUERY_KEY,
      });
      searchQueries.forEach(([queryKey, data]) => {
        if (!data) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueryData(queryKey, (old: any) => {
          if (!old?.listings) return old;
          return {
            ...old,
            listings: old.listings.map((listing: Listing) =>
              listing[idField as keyof Listing] === idToMatch
                ? {
                    ...listing,
                    notes: listing.notes?.filter((n: Note) => n.id !== noteId) || [],
                  }
                : listing
            ),
          };
        });
      });

      return { previousListings, previousDetails };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousListings) {
        queryClient.setQueryData(
          [SEARCH_LISTING_QUERY_KEY],
          context.previousListings
        );
      }
      if (context?.previousDetails) {
        queryClient.setQueryData(
          [LISTING_DETAILS_QUERY_KEY],
          context.previousDetails
        );
      }
      toast({
        variant: "destructive",
        title: "Failed to delete note",
        description: "There was an error deleting the note. Please try again.",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [SEARCH_LISTING_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [LISTING_DETAILS_QUERY_KEY] });
    },
  });
};

export const useToggleContactIsDisabledMutation = ({
  productType,
}: {
  productType: EnumProductTypes;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["Listing:TogglePhoneVisibility"],
    mutationFn: async ({
      phone,
      propertyId,
      isDisabled,
      listingId: _listingId,
    }: {
      phone: string;
      listingId: string;
      propertyId: string;
      isDisabled: boolean;
    }) => {
      return fetcher({
        url: `/api/${productType}/hide-phone`,
        method: "POST",
        body: {
          phone,
          property_id: propertyId,
          hidden: isDisabled,
        },
      });
    },
    onMutate: async ({ phone, listingId, isDisabled }) => {
      await queryClient.cancelQueries({
        queryKey: [LISTING_DETAILS_QUERY_KEY],
      });
      await queryClient.cancelQueries({ queryKey: [SEARCH_LISTING_QUERY_KEY] });

      const previousDetails = queryClient.getQueryData([
        LISTING_DETAILS_QUERY_KEY,
      ]);

      // Get all search listing queries that match the base key
      const searchQueries = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === SEARCH_LISTING_QUERY_KEY,
      });

      // Store previous data for all matching queries
      const previousSearchData = searchQueries.map(([queryKey, data]) => ({
        queryKey,
        data,
      }));

      // Update each matching search query
      searchQueries.forEach(([queryKey, _data]) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueryData(queryKey, (old: any) => {
          if (!old?.listings) return old;
          return {
            ...old,
            listings: old.listings.map((listing: Listing) =>
              listing.id === listingId
                ? {
                    ...listing,
                    contacts: listing.contacts?.map((contact) =>
                      contact.phone === phone
                        ? { ...contact, hidden: isDisabled }
                        : contact
                    ),
                  }
                : listing
            ),
          };
        });
      });

      const detailsQueries = queryClient.getQueriesData({
        queryKey: [LISTING_DETAILS_QUERY_KEY],
      });
      detailsQueries.forEach(([queryKey, data]) => {
        const listing = data as Listing;
        if (listing?.id === listingId) {
          queryClient.setQueryData(queryKey, {
            ...listing,
            contacts: listing.contacts?.map((contact) =>
              contact.phone === phone
                ? { ...contact, hidden: isDisabled }
                : contact
            ),
          });
        }
      });

      return { previousDetails, previousSearchData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousDetails) {
        queryClient.setQueryData(
          [LISTING_DETAILS_QUERY_KEY],
          context.previousDetails
        );
      }
      if (context?.previousSearchData) {
        // Restore all search queries to their previous state
        context.previousSearchData.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [LISTING_DETAILS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_LISTING_QUERY_KEY] });
    },
  });
};
